import React, { useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  IconButton,
  Button,
  Stack,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useColorModeValue,
} from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import LanguageSelector from '@/components/common/LanguageSelector';

interface HeaderProps {
  onOpenSidebar: () => void;
}

const HeaderComponent: React.FC<HeaderProps> = ({ onOpenSidebar }) => {
  const { t } = useTranslation();
  const { user, logout, isAuthenticated } = useAuthStore();
  
  // Memoize color values to prevent unnecessary recalculations
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'white');
  
  // Using useCallback to ensure stable function references
  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);
  
  return (
    <Box
      px={4}
      py={2}
      bg={bgColor}
      borderBottomWidth="1px"
      borderBottomColor={borderColor}
    >
      <Flex alignItems="center" justifyContent="space-between">
        {isAuthenticated && (
          <IconButton
            aria-label="Open Sidebar"
            icon={<HamburgerIcon />}
            display={{ base: 'flex', md: 'none' }}
            onClick={onOpenSidebar}
          />
        )}
        
        <Text
          fontSize="lg"
          fontWeight="bold"
          color={textColor}
          ml={isAuthenticated ? 0 : 4}
        >
          
        </Text>
        
        {isAuthenticated && (
          <Stack direction="row" spacing={4} alignItems="center">
            <LanguageSelector />
            <Menu>
              <MenuButton
                as={Button}
                rounded="full"
                variant="link"
                cursor="pointer"
                minW={0}
              >
                <Flex alignItems="center" gap={2}>
                  <Avatar size="sm" name={user?.name} />
                  <Text display={{ base: 'none', md: 'block' }}>
                    {user?.name}
                  </Text>
                </Flex>
              </MenuButton>
              <MenuList>
                <Text px={3} py={2} fontWeight="medium">
                  {user?.email}
                </Text>
                <Text px={3} pb={2} fontSize="sm" color="gray.500">
                  {user?.role === 'SYSTEM' ? t('users.system') : t('users.tenantAdmin')}
                </Text>
                <MenuDivider />
                <MenuItem onClick={handleLogout}>{t('common.logout')}</MenuItem>
              </MenuList>
            </Menu>
          </Stack>
        )}
      </Flex>
    </Box>
  );
};

// Memoize the Header component
const Header = React.memo(HeaderComponent);

export default Header; 